/**
 * Single source of truth for the purchase-configurator funnel
 * (email → pet name → photo → customization → preview → checkout).
 * Every step reads/writes the SAME sessionStorage key by merging into it,
 * so data accumulates across pages instead of each step having its own
 * separate storage. Load this script before any step-specific script.
 *
 * Fase 1B closing fix: sessionStorage alone survives a REFRESH but not
 * closing the tab/browser (the PDF roadmap's own criterion for this phase
 * is explicit: "cerrar/refrescar el navegador no debe destruir la
 * identidad del diseño"). Every write here is now mirrored to localStorage
 * too, and a read that finds sessionStorage empty (a brand new tab — the
 * signature of "the browser was closed and reopened", as opposed to a
 * same-tab refresh, which never empties sessionStorage) falls back to
 * localStorage's last known state and reseeds sessionStorage from it. This
 * restores the WHOLE funnel state at once (email/petName/petNotes/
 * designId/idempotencyKey/generationResult/...), not just designId in
 * isolation — recovering only the id would still fail generation-page.js's
 * own "missing email/petName" guard. The photo file itself already
 * survives on its own via funnel-photo-db.js (IndexedDB, not
 * sessionStorage), so this is the one remaining gap for that criterion.
 */
window.PfFunnel = (() => {
  const KEY = 'pf_funnel_state';

  function readFrom(storage) {
    try {
      return JSON.parse(storage.getItem(KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function get() {
    const fromSession = readFrom(sessionStorage);
    if (Object.keys(fromSession).length > 0) return fromSession;

    // sessionStorage is empty: either genuinely the first visit, or the
    // customer closed the tab/browser and came back. Either way, restoring
    // from localStorage (if it has anything) is safe — a real first visit
    // simply finds it empty too and nothing changes.
    const fromLocal = readFrom(localStorage);
    if (Object.keys(fromLocal).length > 0) {
      try {
        sessionStorage.setItem(KEY, JSON.stringify(fromLocal));
      } catch (e) {
        // sessionStorage unavailable (private browsing, etc.) — still
        // return the recovered state below, just without re-caching it.
      }
    }
    return fromLocal;
  }

  function set(partial) {
    const next = Object.assign({}, get(), partial, { updatedAt: Date.now() });
    const serialized = JSON.stringify(next);
    try {
      sessionStorage.setItem(KEY, serialized);
    } catch (e) {
      // sessionStorage unavailable (private browsing, etc.) — the funnel
      // still proceeds, just without cross-page persistence.
    }
    try {
      localStorage.setItem(KEY, serialized);
    } catch (e) {
      // localStorage unavailable — the funnel still proceeds within this
      // tab via sessionStorage, it just won't survive closing the browser.
    }
    return next;
  }

  return { get, set };
})();
